import { StyleVariableValueValue } from '@wix/document-services-types';
import {
  ControlService,
  CssPropertyType,
  SpacingStyleValue,
  StyleDefinition,
} from '../../types';
import {
  isShadowStyleValue,
  isSpacingStyleValue,
  valueSet,
  defaultValueSet,
  isGapShorthandValue,
  isPaddingValue,
} from './valueGuards';

export const getValidStyleIds = (
  styles: ControlService['styles'],
  styleDefinitions: ControlService['styleDefinitions'],
  cssPropertyTypes: CssPropertyType[],
): string[] => {
  return Object.keys(styleDefinitions).filter((styleId) => {
    const validProperty = cssPropertyTypes.includes(
      styleDefinitions[styleId].cssPropertyType,
    );

    const valueExists = valueSet(
      getShorthandStyleValues(styles[styleId], styleDefinitions[styleId]) ??
        getStyleValue(styles[styleId], styleDefinitions[styleId]),
    ); // TODO values that not exist should be filtered out on the host level
    return validProperty && valueExists;
  });
};
class StylesValueResolver {
  private styleObject: { value: StyleVariableValueValue } | undefined;
  private defaultValue: string | undefined;
  constructor(
    styleObject: { value: StyleVariableValueValue } | undefined,
    styleDefinition: StyleDefinition,
  ) {
    this.styleObject = styleObject;
    this.defaultValue = styleDefinition?.defaultValue;
  }

  private getValue() {
    if (isSpacingStyleValue(this.styleObject?.value)) {
      return this.styleObject.value;
    }
    if (isShadowStyleValue(this.styleObject?.value)) {
      return this.styleObject.value;
    }
    return undefined;
  }

  private getDefaultValue() {
    if (
      isSpacingStyleValue(this.styleObject?.value) &&
      defaultValueSet(this.defaultValue)
    ) {
      // @ts-expect-error - TODO: ds-types-bump
      const defautlValue: SpacingStyleValue = {
        type: 'Length',
        value: parseInt(this.defaultValue, 10),
        unit: 'px',
      };
      return defautlValue;
    }
    return undefined;
  }

  resolveValue() {
    const valueFromStyles = this.getValue();
    const defaultValue = this.getDefaultValue();
    return valueFromStyles ?? defaultValue;
  }
}

class StylesShorthandValuesResolver {
  private styleObject: { value: StyleVariableValueValue } | undefined;
  constructor(styleObject: { value: StyleVariableValueValue } | undefined) {
    this.styleObject = styleObject;
  }

  private getShorthandValues() {
    if (isPaddingValue(this.styleObject?.value)) {
      return this.styleObject.value;
    }

    if (isGapShorthandValue(this.styleObject?.value)) {
      return this.styleObject.value;
    }
    return undefined;
  }

  resolveValue() {
    const shortHandValues = this.getShorthandValues();
    // TODO: resolve default shorthand values
    return shortHandValues;
  }
}

export const getStyleValue = (
  styleObject: { value: StyleVariableValueValue } | undefined,
  styleDefinition: StyleDefinition,
) => {
  const resolver = new StylesValueResolver(styleObject, styleDefinition);
  return resolver.resolveValue();
};

export const getShorthandStyleValues = (
  styleObject: { value: StyleVariableValueValue } | undefined,
  _styleDefinition: StyleDefinition,
) => {
  const resolver = new StylesShorthandValuesResolver(styleObject);
  return resolver.resolveValue();
};

export const findSharedValue = (
  styleIds: string[],
  resolver: (styleId: string) => number | undefined,
  isEqual: (currentValue: number, sharedValue: number) => boolean,
): number | undefined => {
  let sharedValue: number | undefined;

  for (const styleId of styleIds) {
    const currentValue = resolver(styleId);

    if (!valueSet(sharedValue) && valueSet(currentValue)) {
      sharedValue = currentValue;
      continue;
    }

    if (
      valueSet(currentValue) &&
      valueSet(sharedValue) &&
      !isEqual(currentValue, sharedValue)
    ) {
      return undefined;
    }
  }

  return sharedValue;
};
